import cx from "classnames";
import { useOtpFlow } from "features/auth/hooks/useOtpFlow";
import type { OtpFlowName } from "features/auth/utils/types";
import { AnimatePresence, motion, useAnimation } from "motion/react";
import type { OTPInputProps as BaseOTPInputProps, SlotProps } from "input-otp";
import { OTPInput as BaseOTPInput, REGEXP_ONLY_DIGITS } from "input-otp";
import pluralize from "pluralize";
import React, { useEffect, useRef } from "react";

import * as styles from "./OTPInput.module.scss";

type OTPInputProps = {
  label?: string;
  maxLength?: number;
  flow: OtpFlowName;
  hasError?: boolean;
  lightModeSlots?: boolean;
  className?: string;
} & Pick<
  BaseOTPInputProps,
  | "inputMode"
  | "pattern"
  | "defaultValue"
  | "autoFocus"
  | "onChange"
  | "value"
  | "onComplete"
  | "disabled"
>;

type OTPSlotProps = {
  hasError: boolean;
  lightModeSlots?: boolean;
} & SlotProps;

const shakeAnimation = {
  x: [0, -8, 8, -8, 8, 0], // shake left/right
  transition: { duration: 0.4 },
};

const AttemptsCounter = ({ count }: { count: number }) => {
  return count < 2 ? (
    <AnimatePresence mode="popLayout">
      <motion.div
        key={count}
        initial={{ y: "-100%", opacity: 0 }}
        animate={{ y: "0%", opacity: 1 }}
        exit={{ y: "100%", opacity: 0 }}
        transition={{
          y: { type: "spring", stiffness: 300, damping: 25 },
          opacity: { duration: 0.2 },
        }}
      >
        {count}
      </motion.div>
    </AnimatePresence>
  ) : (
    <div>{count}</div>
  );
};

const OTPInput = ({ label, flow, ...props }: OTPInputProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const controls = useAnimation();

  const otp = useOtpFlow(flow);
  const status = otp.store((s) => s.status);
  const remainingAttempts = otp.store((s) => s.session?.remainingAttempts);

  const showError = status === "retrying" && !!remainingAttempts;

  useEffect(() => {
    if (showError) {
      controls.start(shakeAnimation);
    }
  }, [showError, remainingAttempts, controls]);

  useEffect(() => {
    if (status === "verified") {
      inputRef.current?.blur();
      return;
    }

    if (!remainingAttempts) return;

    inputRef.current?.focus();
  }, [remainingAttempts, status]);

  return (
    <div>
      {label && <div className={styles.label}>{label}</div>}

      <BaseOTPInput
        ref={inputRef}
        maxLength={props.maxLength ?? 6}
        pattern={props.pattern || REGEXP_ONLY_DIGITS}
        id={label}
        aria-label={label}
        autoFocus={props.autoFocus}
        data-1p-ignore
        containerClassName={cx(styles.containerOverride, props.className, {
          [styles.hasError]: showError,
        })}
        render={({ slots }) =>
          slots.map((slot, idx) => (
            <OTPSlot
              key={idx}
              {...slot}
              hasError={showError}
              lightModeSlots={props.lightModeSlots}
              controls={controls}
            />
          ))
        }
        {...props}
      />

      <AnimatePresence>
        {showError && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{
              height: { duration: 0.2 },
              opacity: { duration: 0.15 },
            }}
            className={styles.overflowHidden}
          >
            <div className={styles.otpError}>
              <div className={styles.otpErrorInner}>
                Incorrect code. You have{" "}
                <AttemptsCounter count={remainingAttempts ?? 0} />
                {pluralize("attempt", remainingAttempts)} remaining.
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const OTPSlot = ({
  hasError,
  lightModeSlots,
  isActive,
  char,
  placeholderChar,
  hasFakeCaret,
  controls,
}: OTPSlotProps & { controls?: any }) => {
  return (
    <motion.div
      animate={controls}
      className={cx(styles.otpSlot, {
        [styles.hasError]: hasError,
        [styles.isLight]: lightModeSlots,
        [styles.isActive]: isActive,
      })}
    >
      <div
        className={cx(styles.inputValue, {
          [styles.hasError]: hasError,
          [styles.isActive]: isActive,
        })}
      >
        {char ?? placeholderChar}
      </div>
      {hasFakeCaret && <FakeCaret />}
    </motion.div>
  );
};

const FakeCaret = () => (
  <div className={styles.caretContainer}>
    <div className={styles.caret} />
  </div>
);

export { OTPInput, OTPSlot };
